import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty, CommandGroup, CommandItem, CommandSeparator
} from "@/components/ui/command";
import { Search, TrendingUp, Bitcoin, Star } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import { useAuth } from "@/lib/auth";
import { cn } from "@/lib/utils";

/** Global ⌘K palette. Lists all pairs, keyboard navigable. */
export default function CommandSearch({ pairs = [] }) {
  const { t, pn } = useI18n();
  const { favorites } = useAuth();
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onKey = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === "k") {
        e.preventDefault();
        setOpen((o) => !o);
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  const forex = pairs.filter((p) => !p.symbol.endsWith("/USDT"));
  const crypto = pairs.filter((p) => p.symbol.endsWith("/USDT"));

  const go = (symbol) => {
    const [from, to] = symbol.split("/");
    setOpen(false);
    navigate(`/pair/${from}/${to}`);
  };

  return (
    <>
      <button
        onClick={() => setOpen(true)}
        data-testid="search-trigger"
        className="hidden md:flex items-center gap-2 h-8 px-3 rounded-sm border border-zinc-800 bg-zinc-900/60 text-xs text-zinc-500 hover:text-white hover:border-zinc-700 transition-colors"
      >
        <Search className="h-3.5 w-3.5" />
        <span>{t("search_placeholder")}</span>
        <kbd className="ms-2 px-1.5 py-0.5 rounded-sm bg-zinc-800 text-[10px] font-mono">⌘K</kbd>
      </button>
      <button
        onClick={() => setOpen(true)}
        data-testid="search-trigger-mobile"
        className="md:hidden p-2 rounded-sm border border-zinc-800 bg-zinc-900/60 text-zinc-500 hover:text-white"
        aria-label="Search"
      >
        <Search className="h-4 w-4" />
      </button>

      <CommandDialog open={open} onOpenChange={setOpen} className="bg-[#121212] border-zinc-800">
        <CommandInput placeholder={t("search_placeholder")} data-testid="search-input" />
        <CommandList>
          <CommandEmpty>{t("search_no_results")}</CommandEmpty>

          {favorites.size > 0 && (
            <>
              <CommandGroup heading={t("favorites")}>
                {pairs.filter((p) => favorites.has(p.symbol)).map((p) => (
                  <PairItem key={"fav-" + p.symbol} p={p} onSelect={() => go(p.symbol)} name={pn(p.symbol)} favorite />
                ))}
              </CommandGroup>
              <CommandSeparator className="bg-zinc-800" />
            </>
          )}

          <CommandGroup heading={t("type_forex")}>
            {forex.map((p) => (
              <PairItem key={p.symbol} p={p} onSelect={() => go(p.symbol)} name={pn(p.symbol)} favorite={favorites.has(p.symbol)} />
            ))}
          </CommandGroup>
          <CommandSeparator className="bg-zinc-800" />
          <CommandGroup heading={t("type_crypto")}>
            {crypto.map((p) => (
              <PairItem key={p.symbol} p={p} onSelect={() => go(p.symbol)} name={pn(p.symbol)} favorite={favorites.has(p.symbol)} />
            ))}
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
}

function PairItem({ p, onSelect, name, favorite }) {
  const isCrypto = p.symbol.endsWith("/USDT");
  const sig = p.signal;
  const color = sig === "buy" ? "text-emerald-400" : sig === "sell" ? "text-red-400" : "text-amber-400";
  return (
    <CommandItem
      value={`${p.symbol} ${name || ""}`}
      onSelect={onSelect}
      data-testid={`search-item-${p.symbol.replace("/", "")}`}
      className="flex items-center gap-3 cursor-pointer"
    >
      {isCrypto
        ? <Bitcoin className="h-3.5 w-3.5 text-amber-500" />
        : <TrendingUp className="h-3.5 w-3.5 text-emerald-500" />}
      <div className="flex-1">
        <div className="ticker font-bold text-white text-sm">{p.symbol}</div>
        <div className="text-[10px] text-zinc-500 truncate">{name}</div>
      </div>
      {favorite && <Star className="h-3 w-3 text-amber-400 fill-amber-400" />}
      <div className="flex items-center gap-2">
        <span className={cn("num text-xs font-bold", color)}>{sig?.toUpperCase()}</span>
        <span className="num text-xs text-zinc-400">{p.price?.toFixed(p.price > 50 ? 3 : 5)}</span>
      </div>
    </CommandItem>
  );
}
